import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BasecampLayout } from '../../components/Layout';
import { Card, Spinner, Btn, Input, Stat, StatRow } from '../../components/UI';
import { api, getUser } from '../../utils/api';
import { formatRange } from '../../utils/outlookCalendar';

// Dashboard — "what is on my plate today?"
// Left column = the work (today's next steps, the pipeline, my own tasks);
// right column = the people & me (events, messages, forum, first-year KPIs, grow/contribute).
// Every row is a door: it opens the exact place to act (a customer, a chat, a post).

const PHASES = [
  { key: 'E', label: 'Explore', tagline: 'promote & prospect', shade: 'var(--ink-faint)' },
  { key: 'M', label: 'Master', tagline: 'sell & adapt', shade: 'var(--ink-soft)' },
  { key: 'D', label: 'Deliver', tagline: 'excellence in the room', shade: 'var(--ink)' },
];

// Stage → phase by name (stages come from the sales process). Completed is its own bucket:
// after delivery the journey loops back to Explore for the next need.
function phaseOf(p) {
  const s = (p.stage || '').toLowerCase();
  if (/complete/.test(s)) return 'C';
  if (p.closedWonDate || /deliver|won|graduat|live/.test(s)) return 'D';
  if (/design|profile|proposal|programme|program|closing|negoti|offer|adapt/.test(s)) return 'M';
  return 'E';
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

const fmtDue = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(1, m)} min ago`;
  if (m < 1440) return `${Math.round(m / 60)} h ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// ── small building blocks ─────────────────────────────────────────────────
function Panel({ title, action, children, style }) {
  return (
    <Card style={{ padding: '18px 20px', ...style }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: 0, color: 'var(--ink)' }}>{title}</h2>
        {action}
      </div>
      {children}
    </Card>
  );
}
const MoreLink = ({ to, children }) => <Link to={to} style={{ fontSize: '0.78rem', color: 'var(--ink-soft)', textDecoration: 'none', whiteSpace: 'nowrap' }}>{children} →</Link>;
const Empty = ({ children }) => <div style={{ fontSize: '0.83rem', color: 'var(--ink-faint)', padding: '6px 0' }}>{children}</div>;

function Row({ to, onClick, children, last }) {
  const navigate = useNavigate();
  return (
    <div role="button" tabIndex={0} onClick={onClick || (() => navigate(to))}
      onKeyDown={e => { if (e.key === 'Enter') (onClick || (() => navigate(to)))(); }}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 4px', borderBottom: last ? 'none' : '1px solid var(--line)', cursor: 'pointer', borderRadius: 4 }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--canvas)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
      {children}
    </div>
  );
}

// Calendar-leaf date block for events.
function DateLeaf({ date }) {
  const d = date ? new Date(date) : null;
  return (
    <div style={{ width: 42, flexShrink: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--line)', textAlign: 'center', background: 'var(--canvas-white)' }}>
      <div style={{ background: 'var(--ink)', color: '#fff', fontSize: '0.56rem', fontWeight: 700, letterSpacing: '0.08em', padding: '2px 0', textTransform: 'uppercase' }}>
        {d ? d.toLocaleDateString('en-GB', { month: 'short' }) : 'tba'}
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', lineHeight: 1.4, color: 'var(--ink)' }}>{d ? d.getDate() : '–'}</div>
    </div>
  );
}

export default function MyLine() {
  const navigate = useNavigate();
  const user = getUser() || {};
  const firstName = (user.name || '').trim().split(/\s+/)[0] || 'there';

  const [projects, setProjects] = useState(null);
  const [clients, setClients] = useState([]);
  const [daysDelivered, setDaysDelivered] = useState(0);
  const [week, setWeek] = useState({ overdue: [], thisWeek: [] });
  const [events, setEvents] = useState([]);
  const [convos, setConvos] = useState(null);   // null = chat not available yet
  const [posts, setPosts] = useState(null);     // null = forum not available yet
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');

  const loadTasks = () => api.getTasks().then(d => setTasks(d.tasks || [])).catch(() => setTasks([]));

  useEffect(() => {
    api.explore.getProjects().then(d => setProjects(d.projects || [])).catch(() => setProjects([]));
    api.explore.getClients().then(d => setClients(d.clients || [])).catch(() => setClients([]));
    // Training days = delivery days actually logged (Delivering step), not the registry's free text.
    api.deliver.getMyClients()
      .then(d => setDaysDelivered((d.clients || []).reduce((n, c) => n + c.days.filter(x => x.status === 'delivered').length, 0)))
      .catch(() => setDaysDelivered(0));
    api.explore.getMyWeek().then(d => setWeek({ overdue: d.overdue || [], thisWeek: d.thisWeek || [] })).catch(() => { });
    api.getEvents().then(d => setEvents((d.events || []).filter(e => e.signedUp && e.status === 'scheduled'))).catch(() => setEvents([]));
    api.chat.conversations().then(setConvos).catch(() => setConvos(null));
    api.forum.posts().then(setPosts).catch(() => setPosts(null));
    loadTasks();
  }, []);

  async function addTask(e) {
    e.preventDefault();
    if (!newTask.trim()) return;
    try { await api.createTask(newTask); setNewTask(''); loadTasks(); } catch (err) { alert(err.message); }
  }
  async function toggleTask(t) { try { await api.toggleTask(t.id, !t.done); loadTasks(); } catch (err) { alert(err.message); } }
  async function removeTask(t) { try { await api.deleteTask(t.id); loadTasks(); } catch (err) { alert(err.message); } }

  if (projects === null) {
    return <BasecampLayout><div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div></BasecampLayout>;
  }

  // ── derived ─────────────────────────────────────────────────────────────
  const byPhase = { E: [], M: [], D: [], C: [] };
  projects.forEach(p => byPhase[phaseOf(p)].push(p));
  const active = byPhase.E.length + byPhase.M.length + byPhase.D.length;
  const unread = (convos || []).reduce((n, c) => n + (c.unread || 0), 0);
  const upcoming = [...events].sort((a, b) => {
    if (!a.startsAt) return 1; if (!b.startsAt) return -1;
    return new Date(a.startsAt) - new Date(b.startsAt);
  }).filter(e => !e.startsAt || new Date(e.endsAt || e.startsAt) >= new Date());
  const openTasks = tasks.filter(t => !t.done);

  const summary = [
    week.overdue.length && `${week.overdue.length} overdue`,
    week.thisWeek.length && `${week.thisWeek.length} due this week`,
    unread && `${unread} unread message${unread > 1 ? 's' : ''}`,
    upcoming.length && `${upcoming.length} upcoming event${upcoming.length > 1 ? 's' : ''}`,
  ].filter(Boolean);

  // First-year KPIs, read from live data where we reliably can.
  const kpis = [
    [new Set(projects.map(p => p.clientId || p.customer).filter(Boolean)).size, 'companies prospected'],
    [daysDelivered, 'training days delivered'],
    [projects.filter(p => (p.nextStep || '').trim()).length, 'next steps open'],
    [clients.filter(c => { const f = (c.far || '').toLowerCase(); return f && !/none|no\b|closed|done|complete/.test(f); }).length, 'FAR asks open'],
  ];

  const dueRows = [...week.overdue.map(a => ({ ...a, late: true })), ...week.thisWeek];

  return (
    <BasecampLayout>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: '0.74rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-faint)', fontWeight: 600 }}>
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.85rem', color: 'var(--ink)', lineHeight: 1.15, margin: '4px 0 6px' }}>
            {greeting()}, {firstName}
          </h1>
          <div style={{ fontSize: '0.92rem', color: 'var(--ink-soft)' }}>
            {summary.length ? summary.join(' · ') : 'Nothing urgent today — a good day to prospect.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="outline" size="sm" onClick={() => navigate('/news/events')}>Events</Btn>
          <Btn size="sm" onClick={() => navigate('/customers/journeys')}>+ New project</Btn>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* ── Left: the work ─────────────────────────────────────────────── */}
        <div style={{ flex: '2 1 460px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>

          <Panel title="Today" action={<MoreLink to="/customers/week">My week</MoreLink>}>
            {dueRows.length === 0 ? (
              <Empty>No next steps due this week. Open a customer in <Link to="/customers/journeys" style={{ color: 'var(--ink)' }}>My Pipeline</Link> to plan the next one.</Empty>
            ) : dueRows.slice(0, 6).map((a, i, arr) => (
              <Row key={a.id} to={`/customers/${a.projectId}`} last={i === arr.length - 1}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: a.late ? 'var(--accent, #c8102e)' : 'var(--ink-soft)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--ink-soft)' }}>{a.projectName}{a.customer ? ` · ${a.customer}` : ''}</div>
                </div>
                <span style={{ fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap', color: a.late ? 'var(--accent, #c8102e)' : 'var(--ink-soft)' }}>{a.late ? 'overdue · ' : ''}{fmtDue(a.dueDate)}</span>
              </Row>
            ))}
            {dueRows.length > 6 && <div style={{ marginTop: 8 }}><MoreLink to="/customers/week">{dueRows.length - 6} more</MoreLink></div>}
          </Panel>

          <Panel title="My pipeline" action={<MoreLink to="/customers/journeys">Open My Pipeline</MoreLink>}>
            {active === 0 ? (
              <Empty>No active customers yet — <Link to="/customers/journeys" style={{ color: 'var(--ink)' }}>add your first project</Link>.</Empty>
            ) : (
              <>
                {/* One bar: how the active work is spread across the line */}
                <div style={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', background: 'var(--canvas-warm)', marginBottom: 14 }}>
                  {PHASES.map(ph => byPhase[ph.key].length > 0 && (
                    <div key={ph.key} title={`${ph.label}: ${byPhase[ph.key].length}`} style={{ flex: byPhase[ph.key].length, background: ph.shade }} />
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16 }}>
                  {PHASES.map(ph => {
                    const list = byPhase[ph.key];
                    return (
                      <div key={ph.key} style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                          <span style={{ width: 9, height: 9, borderRadius: 2, background: ph.shade, flexShrink: 0, alignSelf: 'center' }} />
                          <span style={{ fontWeight: 700, fontSize: '0.86rem' }}>{ph.label}</span>
                          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', marginLeft: 'auto' }}>{list.length}</span>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--ink-faint)', marginBottom: 6 }}>{ph.tagline}</div>
                        {list.slice(0, 4).map(p => (
                          <Link key={p.id} to={`/customers/${p.id}`} style={{ display: 'block', padding: '5px 0', borderTop: '1px solid var(--line)', textDecoration: 'none', color: 'var(--ink)' }}>
                            <div style={{ fontSize: '0.84rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.customer || p.name}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--ink-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.stage || '—'}{p.nextStep ? ` → ${p.nextStep}` : ''}</div>
                          </Link>
                        ))}
                        {list.length > 4 && <div style={{ paddingTop: 4 }}><MoreLink to="/customers/journeys">{list.length - 4} more</MoreLink></div>}
                        {list.length === 0 && <div style={{ fontSize: '0.76rem', color: 'var(--ink-faint)', borderTop: '1px solid var(--line)', paddingTop: 5 }}>—</div>}
                      </div>
                    );
                  })}
                </div>
                {byPhase.C.length > 0 && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--ink-soft)', marginTop: 12 }}>
                    ✓ {byPhase.C.length} completed — back in Explore for the customer’s next need.
                  </div>
                )}
              </>
            )}
          </Panel>

          <Panel title="Ongoing things" action={openTasks.length ? <span style={{ fontSize: '0.78rem', color: 'var(--ink-faint)' }}>{openTasks.length} open</span> : null}>
            <form onSubmit={addTask} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Input placeholder="Add a task…" value={newTask} onChange={e => setNewTask(e.target.value)} style={{ padding: '7px 11px', fontSize: '0.86rem' }} />
              <Btn type="submit" size="sm">Add</Btn>
            </form>
            {tasks.length === 0 && <Empty>No tasks — you’re all caught up.</Empty>}
            {tasks.map((t, i) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 2px', borderBottom: i === tasks.length - 1 ? 'none' : '1px solid var(--line)' }}>
                <input type="checkbox" checked={t.done} onChange={() => toggleTask(t)} style={{ width: 15, height: 15, cursor: 'pointer' }} />
                <span style={{ flex: 1, fontSize: '0.86rem', textDecoration: t.done ? 'line-through' : 'none', color: t.done ? 'var(--ink-faint)' : 'var(--ink)' }}>
                  {t.title}
                  {t.source === 'system' && <span style={{ marginLeft: 8, fontSize: '0.62rem', padding: '1px 7px', borderRadius: 99, background: 'var(--canvas-warm)', color: 'var(--ink-soft)', fontWeight: 600 }}>SYSTEM</span>}
                </span>
                <button onClick={() => removeTask(t)} aria-label="Remove task" style={{ color: 'var(--ink-faint)', fontSize: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </Panel>
        </div>

        {/* ── Right: the people & me ─────────────────────────────────────── */}
        <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>

          <Panel title="Coming up" action={<MoreLink to="/news/events">All events</MoreLink>}>
            {upcoming.length === 0 ? <Empty>You’re not signed up for anything yet.</Empty>
              : upcoming.slice(0, 4).map((e, i, arr) => (
                <Row key={e.id} to="/news/events" last={i === arr.length - 1}>
                  <DateLeaf date={e.startsAt} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.86rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.title}</div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--ink-soft)' }}>
                      {e.startsAt ? formatRange(e.startsAt, e.endsAt) : (e.when || 'date tba')}{e.location ? ` · ${e.location}` : ''}
                    </div>
                  </div>
                </Row>
              ))}
          </Panel>

          {convos !== null && (
            <Panel title={unread ? `Messages · ${unread} new` : 'Messages'} action={<MoreLink to="/community/chat">Chat</MoreLink>}>
              {convos.length === 0 ? <Empty>No conversations yet.</Empty>
                : convos.slice(0, 3).map((c, i, arr) => (
                  <Row key={c.otherId} to={`/community/chat?with=${c.otherId}`} last={i === arr.length - 1}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: '0.86rem', fontWeight: c.unread ? 700 : 500 }}>{c.name}</span>
                        <span style={{ fontSize: '0.7rem', color: 'var(--ink-faint)', whiteSpace: 'nowrap' }}>{ago(c.lastMessageAt)}</span>
                      </div>
                      <div style={{ fontSize: '0.78rem', color: c.unread ? 'var(--ink)' : 'var(--ink-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.lastMessage}</div>
                    </div>
                    {c.unread > 0 && <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: 'var(--accent)', color: '#fff', fontSize: '0.66rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{c.unread}</span>}
                  </Row>
                ))}
            </Panel>
          )}

          {posts !== null && (
            <Panel title="From the Forum" action={<MoreLink to="/community/forums">Forum</MoreLink>}>
              {posts.length === 0 ? <Empty>No posts yet — start the first topic.</Empty>
                : posts.slice(0, 3).map((p, i, arr) => (
                  <Row key={p.id} to="/community/forums" last={i === arr.length - 1}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.86rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--ink-soft)' }}>{p.categoryName} · {p.replyCount} {p.replyCount === 1 ? 'reply' : 'replies'} · {ago(p.lastActivityAt || p.createdAt)}</div>
                    </div>
                  </Row>
                ))}
            </Panel>
          )}

          <Panel title="My first year">
            <StatRow min={110} style={{ marginBottom: 0, gap: '14px 16px' }}>
              {kpis.map(([n, label]) => <Stat quiet key={label} value={n} label={label} />)}
            </StatRow>
          </Panel>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['What I take', 'Grow', '/grow'], ['What I give', 'Contribute', '/contribute']].map(([kicker, label, to]) => (
              <Card key={to} hover onClick={() => navigate(to)} style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-faint)' }}>{kicker}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.02rem', marginTop: 2 }}>{label} →</div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </BasecampLayout>
  );
}
